import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop component that automatically scrolls the window to the top
 * when the route changes. This component uses multiple attempts at different intervals
 * to ensure reliable scrolling behavior across various scenarios and browser implementations.
 *
 * This component should be used near the top of the app component tree,
 * typically right inside the Router component.
 *
 * AN ADDRESS THAT NAMES A SECTION IS LEFT TO THE PAGE (2026-09-24). A link like `/#features` (every page's
 * footer carries one) asks to land on that section, and the page that owns it scrolls there once it has
 * rendered. The repeats below used to fire after that jump and scroll the page straight back to the top, so
 * `/#features` landed at the top of the home page on every arrival, today's page and the new design's alike
 * (measured in Chromium). The first scroll to the top still happens, so the page starts from its top and not
 * from wherever the previous page was left; only the repeats stand aside. The hash is read from the address
 * itself, not as a dependency: a same-page link that only changes the hash must not scroll anything to the top.
 */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Immediate scroll
    window.scrollTo(0, 0);
    if (window.location.hash.length > 1) return undefined;

    // Create an array of timeouts for multiple scroll attempts
    const timeoutIds = [
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 100),
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 300),
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 500)
    ];

    // Clean up all timeouts on unmount
    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [pathname]);

  return null;
};

export default ScrollToTop;
